import React, { useState, useEffect } from 'react';
import { useTheme } from '../lib/theme';
import { Badge, Card, SectionTitle } from './ui';

var API_URL = "https://supra-intelligence-worker.data-f9f.workers.dev";

function StatCard({ label, value, color, sub }) {
  return React.createElement('div', { style: {
    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 8, padding: '14px 16px', textAlign: 'center', flex: '1 1 130px',
  }},
    React.createElement('div', { style: { fontSize: 22, fontWeight: 700, color: color || '#fff', fontFamily: "'DM Mono', monospace" } }, value),
    React.createElement('div', { style: { fontSize: 9, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 4 } }, label),
    sub && React.createElement('div', { style: { fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: 2 } }, sub)
  );
}

function HBar({ label, value, max, count, color }) {
  var pct = max > 0 ? (value / max) * 100 : 0;
  return React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0' } },
    React.createElement('span', { style: { fontSize: 11, color: 'rgba(255,255,255,0.5)', width: 100, flexShrink: 0 } }, label),
    React.createElement('div', { style: { flex: 1, height: 16, background: 'rgba(255,255,255,0.04)', borderRadius: 4, overflow: 'hidden', position: 'relative' } },
      React.createElement('div', { style: { width: pct + '%', height: '100%', background: color || '#818CF8', borderRadius: 4, transition: 'width 0.6s ease' } }),
      React.createElement('span', { style: { position: 'absolute', right: 6, top: 1, fontSize: 10, color: '#fff', fontFamily: "'DM Mono', monospace" } },
        value !== null && value !== undefined ? Math.round(value * 100) + '%' : 'N/A'
      )
    ),
    React.createElement('span', { style: { fontSize: 10, color: 'rgba(255,255,255,0.3)', width: 35, textAlign: 'right', fontFamily: "'DM Mono', monospace" } }, 'n=' + count)
  );
}

function RepRow({ rep }) {
  var deltaColor = rep.delta > 0 ? '#4ADE80' : rep.delta < 0 ? '#EF4444' : 'rgba(255,255,255,0.3)';
  return React.createElement('div', { style: {
    display: 'grid', gridTemplateColumns: '120px 60px 70px 70px 70px 70px 80px',
    gap: 8, padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', alignItems: 'center', fontSize: 11,
  }},
    React.createElement('span', { style: { fontWeight: 600, color: '#fff' } }, rep.rep),
    React.createElement('span', { style: { color: 'rgba(255,255,255,0.5)', fontFamily: "'DM Mono', monospace" } }, rep.count),
    React.createElement('span', { style: { color: '#818CF8', fontFamily: "'DM Mono', monospace" } }, rep.show_rate_predicted !== null ? Math.round(rep.show_rate_predicted * 100) + '%' : '-'),
    React.createElement('span', { style: { color: '#4ADE80', fontFamily: "'DM Mono', monospace" } }, rep.show_rate_actual !== null ? Math.round(rep.show_rate_actual * 100) + '%' : '-'),
    React.createElement('span', { style: { color: deltaColor, fontFamily: "'DM Mono', monospace" } }, rep.delta !== null ? (rep.delta > 0 ? '+' : '') + Math.round(rep.delta * 100) + '%' : '-'),
    React.createElement('span', { style: { color: '#E8A838', fontFamily: "'DM Mono', monospace" } }, rep.close_rate !== null ? Math.round(rep.close_rate * 100) + '%' : '-'),
    React.createElement('span', { style: { color: '#38BDF8', fontFamily: "'DM Mono', monospace" } }, '$' + (rep.revenue || 0).toLocaleString())
  );
}

export default function PipelineAnalytics() {
  var t = useTheme();
  var dataState = useState(null); var data = dataState[0]; var setData = dataState[1];
  var loadingState = useState(true); var loading = loadingState[0]; var setLoading = loadingState[1];
  var viewState = useState('overview'); var view = viewState[0]; var setView = viewState[1];

  useEffect(function() {
    fetch(API_URL + '/api/analytics')
      .then(function(r) { return r.json(); })
      .then(function(d) { setData(d); setLoading(false); })
      .catch(function(e) { console.error(e); setLoading(false); });
  }, []);

  if (loading) return React.createElement('div', { style: { textAlign: 'center', padding: 60, color: t.c.textFaint } }, 'Loading analytics...');
  if (!data || data.error) return React.createElement('div', { style: { textAlign: 'center', padding: 60, color: t.c.textFaint } }, 'No analytics data available');

  var tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'timing', label: 'Timing Intelligence' },
    { key: 'reps', label: 'Rep Performance' },
    { key: 'sources', label: 'Lead Sources' },
    { key: 'archetypes', label: 'Archetypes' },
    { key: 'accuracy', label: 'Prediction Accuracy' },
  ];

  return React.createElement('div', { style: { maxWidth: 800, margin: '0 auto' } },
    // Tab bar
    React.createElement('div', { style: { display: 'flex', gap: 0, marginBottom: 20, background: t.c.bgCard, borderRadius: 8, overflow: 'hidden', border: '1px solid ' + t.c.borderCard, flexWrap: 'wrap' } },
      tabs.map(function(tab) {
        return React.createElement('button', { key: tab.key, onClick: function() { setView(tab.key); }, style: {
          padding: '10px 16px', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600,
          fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.03em', transition: 'all 0.2s',
          background: view === tab.key ? 'rgba(129,140,248,0.15)' : 'transparent',
          color: view === tab.key ? '#818CF8' : t.c.textFaint,
        }}, tab.label);
      })
    ),

    // === OVERVIEW ===
    view === 'overview' && React.createElement('div', null,
      React.createElement('div', { style: { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 } },
        React.createElement(StatCard, { label: 'Total Leads', value: data.summary.total_leads, color: '#818CF8' }),
        React.createElement(StatCard, { label: 'Show Rate', value: data.summary.show_rate !== null ? Math.round(data.summary.show_rate * 100) + '%' : 'N/A', color: '#4ADE80' }),
        React.createElement(StatCard, { label: 'Close Rate', value: data.summary.close_rate !== null ? Math.round(data.summary.close_rate * 100) + '%' : 'N/A', color: '#E8A838' }),
        React.createElement(StatCard, { label: 'Revenue', value: '$' + (data.summary.total_revenue || 0).toLocaleString(), color: '#38BDF8' }),
        React.createElement(StatCard, { label: 'Avg Deal', value: data.summary.avg_deal_size ? '$' + Math.round(data.summary.avg_deal_size).toLocaleString() : 'N/A', color: '#F472B6' })
      ),

      // Gender breakdown
      data.gender_analysis && data.gender_analysis.length > 0 && React.createElement(Card, { style: { marginBottom: 16 } },
        React.createElement(SectionTitle, null, 'Gender Breakdown'),
        data.gender_analysis.map(function(g) {
          return React.createElement('div', { key: g.gender, style: { display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' } },
            React.createElement('span', { style: { fontSize: 12, color: 'rgba(255,255,255,0.7)' } }, g.gender),
            React.createElement('div', { style: { display: 'flex', gap: 16 } },
              React.createElement('span', { style: { fontSize: 11, color: 'rgba(255,255,255,0.4)', fontFamily: "'DM Mono', monospace" } }, g.count + ' leads'),
              React.createElement('span', { style: { fontSize: 11, color: '#4ADE80', fontFamily: "'DM Mono', monospace" } }, g.show_rate !== null ? Math.round(g.show_rate * 100) + '% show' : '-'),
              React.createElement('span', { style: { fontSize: 11, color: '#E8A838', fontFamily: "'DM Mono', monospace" } }, g.close_rate !== null ? Math.round(g.close_rate * 100) + '% close' : '-')
            )
          );
        })
      )
    ),

    // === TIMING INTELLIGENCE ===
    view === 'timing' && React.createElement('div', null,
      ['speed_to_lead', 'first_response', 'momentum_gap', 'meeting_gap'].map(function(key) {
        var buckets = data.timing_analysis[key] || [];
        if (buckets.length === 0) return null;
        var labels = { speed_to_lead: 'Speed to Lead vs Show Rate', first_response: 'First Response Velocity vs Show Rate', momentum_gap: 'Momentum Gap vs Show Rate', meeting_gap: 'Meeting Gap vs Show Rate' };
        var barColors = { speed_to_lead: '#818CF8', first_response: '#38BDF8', momentum_gap: '#E8A838', meeting_gap: '#F472B6' };
        return React.createElement(Card, { key: key, style: { marginBottom: 16 } },
          React.createElement(SectionTitle, null, labels[key]),
          buckets.map(function(b) {
            return React.createElement(HBar, { key: b.rating, label: b.bucket, value: b.show_rate, max: 1, count: b.count, color: barColors[key] });
          })
        );
      })
    ),

    // === REP PERFORMANCE ===
    view === 'reps' && React.createElement('div', null,
      React.createElement(Card, null,
        React.createElement(SectionTitle, null, 'Rep Performance vs Outcomes'),
        React.createElement('div', { style: {
          display: 'grid', gridTemplateColumns: '120px 60px 70px 70px 70px 70px 80px',
          gap: 8, padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.08)', fontSize: 9,
          color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.08em',
        }},
          React.createElement('span', null, 'Rep'),
          React.createElement('span', null, 'Leads'),
          React.createElement('span', null, 'Pred Show'),
          React.createElement('span', null, 'Actual Show'),
          React.createElement('span', null, 'Delta'),
          React.createElement('span', null, 'Close Rate'),
          React.createElement('span', null, 'Revenue')
        ),
        (data.rep_analysis || []).map(function(rep) {
          return React.createElement(RepRow, { key: rep.email, rep: rep });
        })
      )
    ),

    // === LEAD SOURCES ===
    view === 'sources' && React.createElement('div', null,
      React.createElement(Card, null,
        React.createElement(SectionTitle, null, 'Lead Source Analysis'),
        (data.source_analysis || []).slice(0, 20).map(function(s) {
          return React.createElement('div', { key: s.source, style: { display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', alignItems: 'center' } },
            React.createElement('span', { style: { fontSize: 11, color: 'rgba(255,255,255,0.7)', maxWidth: '40%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, s.source),
            React.createElement('div', { style: { display: 'flex', gap: 16, alignItems: 'center' } },
              React.createElement('span', { style: { fontSize: 11, color: 'rgba(255,255,255,0.4)', fontFamily: "'DM Mono', monospace" } }, s.count + ' leads'),
              React.createElement('span', { style: { fontSize: 11, color: '#4ADE80', fontFamily: "'DM Mono', monospace" } }, s.show_rate !== null ? Math.round(s.show_rate * 100) + '% show' : '-'),
              React.createElement('span', { style: { fontSize: 11, color: '#E8A838', fontFamily: "'DM Mono', monospace" } }, s.close_rate !== null ? Math.round(s.close_rate * 100) + '% close' : '-'),
              React.createElement('span', { style: { fontSize: 11, color: '#38BDF8', fontFamily: "'DM Mono', monospace" } }, '$' + (s.revenue || 0).toLocaleString())
            )
          );
        })
      )
    ),

    // === ARCHETYPES ===
    view === 'archetypes' && React.createElement('div', null,
      React.createElement(Card, null,
        React.createElement(SectionTitle, null, 'Archetype vs Outcomes'),
        (data.archetype_analysis || []).map(function(a) {
          var archColors = { locked_in: '#4ADE80', slow_burn: '#38BDF8', polite_ghost: '#EF4444', fence_sitter: '#E8A838', rescheduler: '#F472B6', obligation_attender: '#818CF8', early_dropout: '#EF4444', external_dependency: '#E8A838' };
          return React.createElement(HBar, { key: a.archetype, label: a.archetype.replace(/_/g, ' '), value: a.show_rate, max: 1, count: a.count, color: archColors[a.archetype] || '#818CF8' });
        })
      )
    ),

    // === PREDICTION ACCURACY ===
    view === 'accuracy' && React.createElement('div', null,
      React.createElement('div', { style: { display: 'flex', gap: 16, marginBottom: 16 } },
        React.createElement(StatCard, {
          label: 'Show Prediction Accuracy',
          value: data.prediction_accuracy.show_accuracy !== null ? Math.round(data.prediction_accuracy.show_accuracy * 100) + '%' : 'N/A',
          color: data.prediction_accuracy.show_accuracy >= 0.7 ? '#4ADE80' : data.prediction_accuracy.show_accuracy >= 0.5 ? '#E8A838' : '#EF4444',
          sub: 'n=' + data.prediction_accuracy.show_sample_size
        }),
        React.createElement(StatCard, {
          label: 'Close Prediction Accuracy',
          value: data.prediction_accuracy.close_accuracy !== null ? Math.round(data.prediction_accuracy.close_accuracy * 100) + '%' : 'N/A',
          color: data.prediction_accuracy.close_accuracy >= 0.7 ? '#4ADE80' : data.prediction_accuracy.close_accuracy >= 0.5 ? '#E8A838' : '#EF4444',
          sub: 'n=' + data.prediction_accuracy.close_sample_size
        })
      ),
      React.createElement(Card, null,
        React.createElement(SectionTitle, null, 'What This Means'),
        React.createElement('p', { style: { fontSize: 12, color: 'rgba(255,255,255,0.6)', lineHeight: 1.7, margin: 0 } },
          'Prediction accuracy measures how often the engine\'s binary call (above/below 50%) matches reality. ',
          data.prediction_accuracy.show_sample_size < 30
            ? 'Sample size is still small (' + data.prediction_accuracy.show_sample_size + ' leads) — accuracy will stabilise as more outcomes are collected. Aim for 100+ leads with outcomes before drawing conclusions.'
            : 'With ' + data.prediction_accuracy.show_sample_size + ' leads measured, this accuracy is statistically meaningful. The engine is being calibrated by real outcomes.'
        )
      )
    )
  );
}
